// Overview page — built LAST from what other pages produce
// The four reconciliation counters, source health, open findings

import { pbAuthedFetch } from '../authHeaders';
import { useQuery } from '@tanstack/react-query';

export function OverviewPage() {
  const { data: summary, isLoading: summaryLoading, error: summaryError, refetch: refetchSummary } = useQuery({
    queryKey: ['reconciliation-summary'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/reconciliation/summary');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: sourcesData, isLoading: sourcesLoading, error: sourcesError } = useQuery({
    queryKey: ['sources'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/sources');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: findingsData, isLoading: findingsLoading, error: findingsError } = useQuery({
    queryKey: ['findings', { limit: 5 }],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/reconciliation/findings?limit=5');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const sources = sourcesData?.sources || [];
  const findings = findingsData?.findings || [];
  const staleCount = sources.filter((s: any) => {
    if (!s.last_successful_run_at) return true;
    const days = (Date.now() - new Date(s.last_successful_run_at).getTime()) / (1000 * 60 * 60 * 24);
    return days > 14;
  }).length;

  const totalCIs = sources.reduce((sum: number, s: any) => sum + (s.total_cis || 0), 0);

  const isLoading = summaryLoading || sourcesLoading || findingsLoading;
  const hasError = summaryError || sourcesError || findingsError;

  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Dashboard</p>
              <h1 className="pagehead__title">ATLAS Overview</h1>
              <div className="pagehead__facts">
                <span className="fact">ITOps Accountability Platform</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading reconciliation data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Dashboard</p>
              <h1 className="pagehead__title">ATLAS Overview</h1>
              <div className="pagehead__facts">
                <span className="fact">ITOps Accountability Platform</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load overview data</h3>
                <p className="state__body">
                  Could not connect to the reconciliation API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetchSummary()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Dashboard</p>
            <h1 className="pagehead__title">ATLAS Overview</h1>
            <div className="pagehead__facts">
              <span className="fact">ITOps Accountability Platform</span>
              <span className="fact">Reconciliation & System of Record</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Completeness statement */}
        <div className="notice notice--info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <p className="notice__title">System Coverage</p>
            <p className="notice__body">
              ATLAS has observed <strong>{totalCIs || 0} CIs</strong> from{' '}
              <strong>{sources.filter((s: any) => s.last_successful_run_at).length} of {sources.length} configured sources</strong>.
              {staleCount > 0 && (
                <> {staleCount} source{staleCount > 1 ? 's' : ''} have not reported in over 14 days.</>
              )}
              {' '}Confidence in this view: {totalCIs > 0 ? 'moderate' : 'no data'}.
            </p>
          </div>
        </div>

        {/* Four reconciliation counters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Reconciliation summary">
          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">Ghosts</p>
              <p className="ind__full">In CMDB, never observed on network</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary?.ghosts ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>Monitor</b> — present in CMDB but not detected on network
            </p>
          </div>

          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">Orphans</p>
              <p className="ind__full">On network, in no system of record</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary?.orphans ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>Monitor</b> — detected but not tracked in any system
            </p>
          </div>

          <div className="ind ind--breach">
            <div className="ind__head">
              <p className="ind__abbr">Unowned</p>
              <p className="ind__full">No ownership edge or separated owner</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary?.unowned ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i></i>
              <b>Action required</b> — no accountable owner identified
            </p>
          </div>

          <div className="ind ind--breach">
            <div className="ind__head">
              <p className="ind__abbr">Conflicts</p>
              <p className="ind__full">Sources disagree on attribute values</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary?.conflicts ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i></i>
              <b>Action required</b> — data inconsistency detected
            </p>
          </div>
        </div>

        {/* Source health strip */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Data Sources</p>
              <h2 className="box__title">Source Health</h2>
            </div>
          </div>
          <div className="box__body">
            {sources.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
                {sources.map((s: any) => {
                  const days = s.last_successful_run_at
                    ? Math.floor((Date.now() - new Date(s.last_successful_run_at).getTime()) / (1000 * 60 * 60 * 24))
                    : -1;
                  const isStale = days > 14 || days === -1;

                  return (
                    <div
                      key={s.id}
                      className="pill"
                      style={{
                        background: isStale ? 'var(--gold-wash)' : 'var(--surface-sub)',
                        borderColor: isStale ? 'var(--gold-soft)' : 'var(--hair)',
                        padding: 'var(--sp-2) var(--sp-3)',
                      }}
                    >
                      <i style={{ background: isStale ? 'var(--gold)' : 'var(--ok)' }}></i>
                      <b style={{ color: 'var(--ink)' }}>{s.display_name}</b>
                      <span style={{ color: isStale ? 'var(--gold)' : 'var(--ink-45)' }}>
                        {days === -1 ? 'Never run' : `${days}d ago`}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <h3 className="state__title">No sources configured</h3>
                <p className="state__body">
                  Start by configuring your first data source to begin tracking infrastructure assets.
                </p>
                <a href="/sources" className="btn btn--primary">Configure Sources</a>
              </div>
            )}
          </div>
        </div>

        {/* Top findings */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Reconciliation</p>
              <h2 className="box__title">Top Findings</h2>
            </div>
            <div className="box__head-actions">
              <a href="/reconciliation" className="btn btn--sm" style={{ background: 'rgba(255,255,255,.1)', borderColor: 'rgba(255,255,255,.2)', color: 'white' }}>
                View all →
              </a>
            </div>
          </div>
          <div className="box__body box__body--flush">
            {findings.length > 0 ? (
              <div className="tbl-scroll">
                <table className="tbl">
                  <caption style={{ padding: 'var(--sp-2) var(--sp-3)', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', color: 'var(--ink-45)', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                    Top 5 reconciliation findings
                  </caption>
                  <thead>
                    <tr>
                      <th><button className="th">Severity</button></th>
                      <th><button className="th">Type</button></th>
                      <th><button className="th">Title</button></th>
                      <th><button className="th">CI</button></th>
                      <th><button className="th th--static">Detected</button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((f: any) => (
                      <tr key={f.id}>
                        <td>
                          <span className={`pill pill--${f.severity === 'critical' || f.severity === 'high' ? 'risk' : f.severity === 'medium' ? 'warn' : 'ok'}`}>
                            <i></i>
                            {f.severity}
                          </span>
                        </td>
                        <td className="td--id">{f.finding_type}</td>
                        <td className="td--name">{f.title}</td>
                        <td className="td--id">{f.canonical_key || '—'}</td>
                        <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                          {new Date(f.detected_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="state__title">No findings yet</h3>
                <p className="state__body">
                  Ingest data from sources to generate reconciliation findings.
                </p>
                <a href="/sources" className="btn btn--primary">Go to Sources</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
